// ─── Layer Naming (Phase 1.8) ─────────────────────────────────
// Auto-derives a human-friendly label for the Layer panel so designers see
// "Background image" / "Invitation card" / "Photo 2" instead of repeated
// generic "Image" entries.
//
// Rules (in order):
//   1. Explicit element.title / element.caption (designer-set)
//   2. Image role (see /lib/utils/imageRoles.js)
//   3. Text element — content + a few keyword hints (Couple names, RSVP,
//      Date, Venue)
//   4. Fallback to a generic per-type label
//
// Per-role-index suffixes ("Photo 1", "Photo 2", …) are computed by the
// caller via the second argument.

import { IMAGE_ROLES } from './imageRoles';

const ROLE_LABEL = {
  [IMAGE_ROLES.SECTION_BACKGROUND]: 'Background image',
  [IMAGE_ROLES.PAPER_CARD]:         'Invitation card',
  [IMAGE_ROLES.PHOTO]:              'Photo',
  [IMAGE_ROLES.DECORATIVE]:         'Decorative element',
  [IMAGE_ROLES.IMAGE_LAYER]:        'Image layer',
};

const KEYWORD_HINTS = [
  { re: /\brsvp\b/i,                       label: 'RSVP button' },
  { re: /^\s*&?\s*$/,                      label: 'Text' },
  { re: /^(date|when)\b/i,                 label: 'Date' },
  { re: /\b(venue|address|location|where)\b/i, label: 'Venue' },
];

/** Try to detect "Couple names" from a title block. */
function looksLikeCoupleNames(text) {
  if (!text) return false;
  // Has an ampersand or " and " between two short tokens (1–24 chars each).
  return /^[\p{L}\s]{1,24}\s*(?:&|\band\b|·)\s*[\p{L}\s]{1,24}\s*$/iu.test(text.trim());
}

/**
 * @param {object} element  — full element shape from v2_content._elements
 * @param {number} indexByRole — 1-based index *within the same role*; pass 1 if you don't want a number.
 * @returns {string}
 */
export function getLayerDisplayName(element, indexByRole = 1) {
  if (!element) return 'Layer';

  // 1. Explicit designer labels win.
  if (element.title && typeof element.title === 'string') return element.title.slice(0, 28);
  if (element.caption && typeof element.caption === 'string' && element.caption.length <= 30) {
    return element.caption;
  }

  // 2. Image roles
  const role = element.role;
  if (role && ROLE_LABEL[role]) {
    if (role === IMAGE_ROLES.PHOTO && indexByRole > 1) return `Photo ${indexByRole}`;
    return ROLE_LABEL[role];
  }

  // 3. Text elements — content + hints
  const text = (element.content && typeof element.content === 'string' ? element.content : element.text) || '';
  const t = text.trim();
  if (element.type === 'title_block' || element.type === 'text_block' || element.type === 'text_note') {
    for (const { re, label } of KEYWORD_HINTS) if (re.test(t)) return label;
    if (looksLikeCoupleNames(t)) return 'Couple names';
    if (t.length === 0) return 'Text';
    return t.length <= 24 ? t : t.slice(0, 24) + '…';
  }

  // 4. Per-type fallback for non-image elements
  switch (element.type) {
    case 'image_card':     return element.src ? 'Photo' : 'Image layer';
    case 'shape':          return 'Shape';
    case 'ornament':       return 'Ornament';
    case 'vinyl_player':   return 'Vinyl player';
    case 'slideshow_element': return 'Slideshow';
    default:               return 'Layer';
  }
}

/**
 * Bulk variant — returns labels keyed by element id, computing the per-role
 * index so "Photo 1 / Photo 2 / Photo 3" works without the caller juggling
 * counters. Iterate in the order elements appear in the section so labels
 * stay stable across renders.
 */
export function computeLayerLabels(elements) {
  const counters = {};
  const out = {};
  (elements || []).forEach((el) => {
    const role = el.role || (el.type === 'image_card' ? IMAGE_ROLES.IMAGE_LAYER : null);
    if (role) counters[role] = (counters[role] || 0) + 1;
    out[el.id] = getLayerDisplayName(el, role ? counters[role] : 1);
  });
  return out;
}
